import React,{useEffect, useState} from 'react';
import { X, Download, FileDown, Plug as Plugin, Info, Copy } from 'lucide-react';
import { Scene } from '../types';
import { customerApi } from '../services/customerApi';
import { PLUGIN_GUIDE_URL } from '../constants/resources';

interface Props {
  scene: Scene;
  isOpen: boolean;
  onClose: () => void;
}

export default function DownloadModal({ scene, isOpen, onClose }: Props) {
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState<number | undefined>(scene.fileSize);
  // Get the presigned download URL when modal opens
  useEffect(() => {
    if (isOpen && scene.status === 'completed') {
      getDownloadUrl();
    }
  }, [isOpen, scene.id, scene.status]);

  const getDownloadUrl = async () => {
    try {
      const response = await customerApi.getGaussianSplatObject(scene.id);
      if (response.success && response.data?.gaussianFile?.signedUrl) {
        setDownloadUrl(response.data.gaussianFile.signedUrl);
        // Update file size if provided by API
        if (response.data.gaussianFile.size) {
          setFileSize(response.data.gaussianFile.size);
        }
      }
    } catch (error) {
      console.error('[DownloadModal] Error getting download URL:', error);
    }
  };

  if (!isOpen) return null;

  // Format file size
  const formatFileSize = (bytes?: number): string => {
    if (bytes === undefined || bytes === 0) return 'Unknown size';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };
  
  // Format date
  const formatDate = (timestamp?: number): string => {
    if (!timestamp) return 'Unknown date';
    
    const date = new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Get file name from path
  const getFileName = (path?: string): string => {
    if (!path) return `${scene.name.replace(/\s+/g, '_')}.gaus`;
    
    const parts = path.split('/');
    return parts[parts.length - 1] || `${scene.name.replace(/\s+/g, '_')}.gaus`;
  };

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text)
      .then(() => alert('Copied to clipboard'))
      .catch(err => console.error('Failed to copy: ', err));
  };

  return (
    <div className="fixed inset-0 z-[1100] overflow-y-auto flex items-center justify-center">
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/70" onClick={onClose}></div>
      
      {/* Modal */}
      <div className="relative bg-[#161513] rounded-none shadow-lg w-full max-w-xl mx-4 max-h-[90vh] overflow-y-auto transform transition-all border border-[#0CB4E4]/30 z-50">
        {/* Header */}
        <div className="p-6 border-b border-[#0CB4E4]/20 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">Download Your 3D Scene</h2>
          <button
            onClick={onClose}
            className="p-2 text-gray-300 hover:text-white rounded-lg transition-colors hover:bg-[#161513]"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Content */}
        <div className="p-6">
          <p className="text-gray-300 mb-6">
            This scene is a .gaus file and requires the Unreal Engine plugin to view and interact.
          </p>
          
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="mb-8">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0CB4E4]/20 rounded-full flex items-center justify-center text-sm text-[#0CB4E4] font-bold">1</span>
                <span>Install Plugin</span>
              </h3>
              
              <div className="bg-[#161513] rounded-none border border-[#0CB4E4]/30 p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Plugin className="w-5 h-5 text-[#0CB4E4]" />
                  <div>
                    <p className="text-white font-medium">Unreal Engine Plugin</p>
                    <p className="text-xs text-gray-400">Compatible with UE 5.4</p>
                  </div>
                </div>
                    
                <a 
                  href="https://ydrive-stratus-releases.s3.eu-central-1.amazonaws.com/Stratus-v0.7.0-win64.zip"
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="px-4 py-2 bg-[#0C5484] text-white rounded-xl hover:bg-[#0CB4E4]/80 transition-colors text-sm font-medium flex items-center gap-1 shadow-md"
                >
                  <Download className="w-4 h-4" />
                  <span>Download Plugin</span>
                </a>
              </div>
            </div>
            
            {/* Step 2 */}
            <div className="mb-8">
              <h3 className="text-white font-medium mb-3 flex items-center gap-2">
                <span className="w-6 h-6 bg-[#0CB4E4]/20 rounded-full flex items-center justify-center text-sm text-[#0CB4E4] font-bold">2</span>
                <span>Download the Scene</span>
              </h3>
              
              <div className="bg-[#161513] rounded-none border border-[#0CB4E4]/30 p-4 mb-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-xs text-gray-400">Scene Name</p>
                    <p className="text-sm text-white font-mono truncate">{getFileName(scene.gaussModelPath)}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-400">Size</p>
                    <p className="text-sm text-white">{formatFileSize(fileSize)}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-400">Created</p>
                    <p className="text-sm text-white">{formatDate(scene.updatedAt)}</p>
                  </div>
                  
                  <div>
                    <p className="text-xs text-gray-400">Type</p>
                    <p className="text-sm text-white">.gaus (Gaussian Splat)</p>
                  </div>
                </div>
                
                {/* Use presigned URL if available, fallback to original logic */}
                {downloadUrl ? (
                  <a 
                    href={downloadUrl}
                    className="w-full py-3 px-4 bg-[#0CB4E4] text-[#161513] rounded-xl hover:bg-[#0CB4E4]/90 transition-colors font-medium flex items-center justify-center gap-2 shadow-md"
                    download
                  >
                    <FileDown className="w-5 h-5" />
                    <span>Download .gaus File</span>
                  </a>
                ) : (
                  <button
                    disabled
                    className="w-full py-3 px-4 bg-gray-500/50 text-gray-300 rounded-xl cursor-not-allowed font-medium flex items-center justify-center gap-2"
                  >
                    <FileDown className="w-5 h-5" />
                    <span>Download not available</span>
                  </button>
                )}
              </div>
              
              {!downloadUrl && (
                <div className="bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-none p-4 text-sm text-[#0CB4E4] flex items-start gap-2">
                  <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>3D model file is not available for this scene yet. Please wait for processing to complete.</span>
                </div>
              )}
            </div>
          </div>
          
          {/* Info Note */}
          <div className="bg-[#0CB4E4]/10 border border-[#0CB4E4]/30 rounded-none p-4 text-sm text-[#0CB4E4] flex items-center gap-2">
            <Info className="w-5 h-5 flex-shrink-0" />
            <div>
              <span>Need help setting up the plugin? </span>
              <a 
                href={PLUGIN_GUIDE_URL}
                target="_blank" 
                rel="noopener noreferrer"
                className="font-medium underline hover:text-[#0CB4E4]/80 transition-colors"
              >
                View Setup Guide
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
